'use client';

import { useMemo } from 'react';
import { stegaClean } from '@sanity/client/stega';
import type { ILaunchEvent } from '@/sanity/lib/definitions';
import { useCampaignLive } from '@/hooks/useCampaignLive';
import {
  resolveLaunchEventAudience,
  type ILaunchEventAudience,
} from '@/lib/launchEvent';

/**
 * Client-side live check for the Launch Event widget campaign, resolved for
 * the page's own audience: German pages follow the German event, every other
 * page the international one (see lib/launchEvent.ts). The two have different
 * end dates, so a German page can already be past its event while the
 * international widget is still counting down.
 *
 * Also used by the homepage to switch the payout toast off while the widget is
 * up, so the two never fight over the same corner.
 *
 * QA/preview override: `?launchPreview=1` forces the widget on before the
 * start date, `?launchPreview=0` hides it while live.
 */

/** Same one-minute cadence as the flash sale — campaigns are scheduled to the
 * minute. The countdown inside the widget ticks on its own 1s interval. */
const CHECK_INTERVAL_MS = 60_000;

export function useLaunchEventLive(
  config: ILaunchEvent | null | undefined,
  language?: string,
): {
  config: ILaunchEvent | null;
  audience: ILaunchEventAudience | null;
  live: boolean;
} {
  // Draft/Presentation mode fetches with stega enabled — invisible characters
  // inside startDate/endDate would break Date parsing (same reason
  // FlashSaleProvider cleans its config).
  const cleaned = useMemo(
    () => (stegaClean(config ?? null) ?? null) as ILaunchEvent | null,
    [config],
  );
  const audience = useMemo(
    () => resolveLaunchEventAudience(cleaned, language),
    [cleaned, language],
  );
  const live = useCampaignLive(audience, 'launchPreview', CHECK_INTERVAL_MS);
  return { config: cleaned, audience, live };
}
